"use client";

import type { useChat } from "@ai-sdk/react";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { useParams } from "next/navigation";
import { getCountryLabels } from "@/app/[lang]/[workspaceId]/sa/_utils/country";
import {
  DeviceLabels,
  SearchAnalyticsDataDateTypeLabels,
  SearchAnalyticsFilterLabels,
  SearchAnalyticsRawDataKindLabels,
  SearchAnalyticsSearchTypeLabels,
} from "@/app/[lang]/[workspaceId]/sa/constants";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import type { CreateSearchAnalytcsViewInput } from "./schema";

const ProjectDataQuery = graphql(`
  query SaViewConfirmProjectData($id: ID!) {
    node(id: $id) {
      __typename
      ... on SearchAnalyticsProject {
        name
        searchAnalytics {
          id
          __typename
          queryGroups {
            id
            __typename
            name
          }
          urlGroups {
            id
            __typename
            name
          }
        }
      }
    }
  }
`);

type CreateSaViewInput = CreateSearchAnalytcsViewInput;

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

export function CreateSearchAnalyticsViewConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const { i18n } = useLingui();
  const params = useParams<{ lang: string }>();
  const lang = (params?.lang as string) || "en";
  const isTarget =
    isToolUIPart(part) && getToolName(part) === "create_search_analytics_view";
  const isInputAvailable = part.state === "input-available";

  const input = (part.input ?? {}) as Partial<CreateSaViewInput>;

  const projectId = input.projectId ?? "";
  // プロジェクト名やクエリ／URLグループ名を表示するために追加情報を取得する
  const [{ data, fetching }] = useQuery({
    query: ProjectDataQuery,
    variables: { id: projectId },
    pause: !(isTarget && isInputAvailable && projectId),
  });
  const project =
    data?.node?.__typename === "SearchAnalyticsProject" ? data.node : null;
  const projectName = project?.name ?? "Unknown";
  const queryGroups = project?.searchAnalytics?.queryGroups ?? [];
  const queryGroupNames = Object.fromEntries(
    queryGroups.map((group) => [group.id, group.name]),
  );
  const urlGroups = project?.searchAnalytics?.urlGroups ?? [];
  const urlGroupNames = Object.fromEntries(
    urlGroups.map((group) => [group.id, group.name]),
  );

  if (!isTarget || !isInputAvailable) return null;

  return (
    <div className="p-4 text-sm">
      <div className="mb-2 font-medium">
        <Trans>Do you want to create a new Search Analytics view?</Trans>
      </div>
      <ul className="mb-4 list-disc pl-5 space-y-1">
        <li>
          <Trans>Name</Trans>: <span className="font-medium">{input.name}</span>
        </li>
        {input.description ? (
          <li>
            <Trans>Description</Trans>:{" "}
            <span className="font-normal">{input.description}</span>
          </li>
        ) : null}
        {projectId ? (
          <li>
            <Trans>Project</Trans>:{" "}
            {fetching ? (
              <Skeleton className="h-4 w-12" />
            ) : (
              <span className="font-medium">{projectName}</span>
            )}
          </li>
        ) : null}

        {/* Optional filters summary */}
        {input.dataDate ? (
          <li>
            <Trans>Saved date</Trans>:
            <span className="font-medium">
              {i18n._(SearchAnalyticsDataDateTypeLabels[input.dataDate.type])}
            </span>
            {input.dataDate.type === "CUSTOM" && input.dataDate.dateRange ? (
              <span className="ml-2 text-muted-foreground">
                ({input.dataDate.dateRange.start} -{" "}
                {input.dataDate.dateRange.end})
              </span>
            ) : null}
          </li>
        ) : null}

        {input.query ? (
          <li>
            <Trans>Query</Trans>:{" "}
            <span className="font-medium">{input.query}</span>
            {input.queryFilterType ? (
              <span className="ml-2 text-muted-foreground">
                {i18n._(SearchAnalyticsFilterLabels[input.queryFilterType])}
              </span>
            ) : null}
          </li>
        ) : null}

        {input.url ? (
          <li>
            <Trans>URL</Trans>:{" "}
            <span className="font-medium break-all">{input.url}</span>
            {input.urlFilterType ? (
              <span className="ml-2 text-muted-foreground">
                {i18n._(SearchAnalyticsFilterLabels[input.urlFilterType])}
              </span>
            ) : null}
          </li>
        ) : null}

        {input.queryGroupIDs && input.queryGroupIDs.length ? (
          <li>
            <Trans>Query Groups</Trans>:{" "}
            <span className="font-medium">
              {input.queryGroupIDs
                .map((id) => queryGroupNames[id] || id)
                .join(", ")}
            </span>
          </li>
        ) : null}

        {input.urlGroupIDs && input.urlGroupIDs.length ? (
          <li>
            <Trans>URL Groups</Trans>:{" "}
            <span className="font-medium">
              {input.urlGroupIDs
                .map((id) => urlGroupNames[id] || id)
                .join(", ")}
            </span>
          </li>
        ) : null}

        {input.searchTypes && input.searchTypes.length ? (
          <li>
            <Trans>Search type</Trans>:{" "}
            <span className="font-medium">
              {input.searchTypes
                .map((t) => {
                  return i18n._(SearchAnalyticsSearchTypeLabels[t]);
                })
                .join(", ")}
            </span>
          </li>
        ) : null}

        {input.devices && input.devices.length ? (
          <li>
            <Trans>Devices</Trans>:{" "}
            <span className="font-medium">
              {input.devices
                .map((d) => {
                  return i18n._(DeviceLabels[d]);
                })
                .join(", ")}
            </span>
          </li>
        ) : null}

        {input.countries && input.countries.length ? (
          <li>
            <Trans>Country</Trans>:{" "}
            <span className="font-medium">
              {input.countries
                .map((c) => {
                  const countryLabels = getCountryLabels(lang);
                  return c in countryLabels
                    ? countryLabels[c as keyof typeof countryLabels]
                    : c;
                })
                .join(", ")}
            </span>
          </li>
        ) : null}

        {input.rawKinds && input.rawKinds.length ? (
          <li>
            <Trans>Search Appearances</Trans>:{" "}
            <span className="font-medium">
              {input.rawKinds
                .map((k) => {
                  return i18n._(SearchAnalyticsRawDataKindLabels[k]);
                })
                .join(", ")}
            </span>
          </li>
        ) : null}

        {input.rawKindsNot && input.rawKindsNot.length ? (
          <li>
            <Trans>Search Appearances to exclude</Trans>:{" "}
            <span className="font-medium">
              {input.rawKindsNot
                .map((k) => {
                  return i18n._(SearchAnalyticsRawDataKindLabels[k]);
                })
                .join(", ")}
            </span>
          </li>
        ) : null}

        {typeof input.isAnonymousQuery === "boolean" ? (
          <li>
            <Trans>Anonymous query</Trans>:{" "}
            <span className="font-medium">
              {input.isAnonymousQuery ? <Trans>Yes</Trans> : <Trans>No</Trans>}
            </span>
          </li>
        ) : null}
      </ul>
      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Create</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
